import { getDocumentCategoryLabel } from '@folio/utils/getDocumentCategoryLabel';
import { useQuery } from '@tanstack/react-query';
import {
  configControllerGetPublicProductConfigQueryOptions,
  policyControllerGetPolicyQueryOptions,
  policyDocumentCollectionControllerGetCollectionStateQueryOptions,
} from 'korint-sdk';
import { CalendarClock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@folio/components/ui/alert';

/** Shows the remaining time to upload missing policyholder documents when a product
 * collects them after signature (documentCollection) rather than requiring them upfront. */
export function DocumentCollectionDeadlineBanner({
  policyId,
  branchId,
}: {
  policyId: string;
  branchId?: string;
}) {
  const { t } = useTranslation();
  // Base branch's real id is the literal string "BASE" (see PolicyAggregateProjection).
  const resolvedBranchId = branchId ?? 'BASE';

  const { data: policy } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, { branchId }),
  });

  const { data: state } = useQuery({
    ...policyDocumentCollectionControllerGetCollectionStateQueryOptions(policyId, {
      branchId: resolvedBranchId,
    }),
    retry: false,
  });

  const { data: requiredDocumentConfigs } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy?.productId!),
    select: data => data.policyholderDocuments ?? [],
    enabled: Boolean(policy?.productId) && Boolean(state?.missingCategories?.length),
  });

  if (!state || state.isComplete || !state.windowEndsAt) {
    return null;
  }

  const remainingMs = new Date(state.windowEndsAt).getTime() - Date.now();
  if (remainingMs <= 0) {
    return null;
  }

  const remainingHours = Math.ceil(remainingMs / (1000 * 60 * 60));
  const remainingTime =
    remainingHours < 48
      ? t('documentCollectionDeadline.remainingHours', { count: remainingHours })
      : t('documentCollectionDeadline.remainingDays', { count: Math.ceil(remainingHours / 24) });

  const missingList = state.missingCategories
    .map(category => getDocumentCategoryLabel(category, requiredDocumentConfigs, t))
    .join(', ');

  return (
    <Alert variant="warning">
      <CalendarClock />
      <AlertDescription>
        {t('documentCollectionDeadline.message', { remainingTime, missingList })}
      </AlertDescription>
    </Alert>
  );
}
